import { ReactElement, useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useLatest } from 'react-use'
import {
  StiffeningMesh,
  LoadMesh,
  VerticalTransmittersMeshConnector,
  TensileTransmittersMeshConnector,
  useModelClickListenerV2,
} from '@scene'
import { find, reduce, filter, reject, isArray } from 'lodash-es'
import { useCameraStore } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import {
  useStructuralPlanningStore,
  useElementLoadStore,
  useModelStore,
  useEditElementStore,
  roofStoreyKey,
  useTransmissionStore,
  useStiffeningStore,
  useControlStore,
  useSelectionStore,
  useTapelineStore,
  isGenericSceneClickMode,
  refusePaneChange,
} from '@editorStores'
import {
  useSelectionMode,
  useHiddenElements,
  useTypeInteraction,
  useModelClickListeners,
  useHighlightFromSelection,
  useHighlightSecondary,
  useMultiSelection,
  useGuidToElementIncludingRips,
  usePositionGuidToWallGuid,
  useSceneColoring,
  useIfcImportSelectionActive,
} from '@editorHooks'
import { VisiblePositions } from 'src/components/scene/VisiblePositions'
import WindLoadCPEAreasMesh from 'src/components/scene/WindLoadCPEAreasMesh'
import {
  useHotkeys_Ctrl_0to9,
  useHotkeys_0to9WithNumpadAndLongPress,
  useHotkeys_ShortAndLongPress,
} from 'src/hooks/useHotkeysShiftX'
import {
  PurlinDrawer3D,
  FloorplanDrawer,
  OpeningsDrawer,
  OpeningEdit3D,
  OrthographicWall,
  VerticalRoofSlabDrawer3D,
  WallLintelDrawer3D,
} from '../'
import { useFilterState } from '../../hooks/useFilterState'
import useStructuralPlanningSelection from '../../hooks/useStructuralPlanningSelection'
import { useVerticalOpeningHelpers } from '../../hooks/useVerticalOpeningHelpers'
import AlignmentHelper from '../AlignmentHelper'
import BeamDrawer3D from '../BeamDrawing/components/BeamDrawer'
import useEmptyModelSceneLabel from '../EmptyModelSceneLabel'
import { modelHasStructuralElements } from '../EmptyModelSceneLabel/utils'
import { VerticalSlabDrawer3D } from '../VerticalSlabs'
import SlabSelection from './SlabSelection'

const StructuralPlanningScene = (): ReactElement | null => {
  const {
    params: {
      mode,
      selectedElements,
      selectedStiffeningElements,
      connectorGuid,
      verticalTransmitter,
      load: activeLoadGuid,
      storey,
      activeTab,
    },
    actions: {
      setElements,
      setStiffeningElement,
      setConnector,
      selectLoad: setLoad,
      setVerticalTransmitter,
      toggleMode,
      reset,
      setStorey,
      multiSelectElements,
      removeMultiSelectElement,
    },
    modes: {
      isStiffeningSelectionMode,
      isWallMode,
      isBeamMode,
      isNeutralMode,
      isLoadMode,
      isVerticalSlabMode,
      isDrawingVerticalRoofMode,
      isDrawingSlabMode,
      isDrawingRoofMode,
      isDrawingPurlinsMode,
      isRoofMode,
      isPurlinMode,
      isColumnMode,
      isStoreySelectionMode,
      isDrawingBeamsMode,
      isDrawingColumnsMode,
      isDrawingWallsMode,
      isDrawingOpeningsMode,
      isEditOpenings3DMode,
      isRipMode,
      isLintelMode,
      isWallRipMode,
      isWallLintelMode,
      isDrawingVerticalSlabsMode,
      isMultiEdit,
    },
  } = useStructuralPlanningSelection()

  const model = useModelStore(state => state.model)
  const showEmptyModelLabel = useMemo(() => !modelHasStructuralElements(model), [model])
  useEmptyModelSceneLabel(showEmptyModelLabel)

  const latestSetElements = useLatest(setElements)

  const { showVerticalSlabs, showSlabs, showVerticalRoofSlabs, showRoofSlabs, showPurlins } =
    useFilterState()

  const { isSelectionMode } = useSelectionMode()

  // STATE

  const { isSelectionMode: isSceneSelectionMode } = useSelectionMode()
  const proposal = useStructuralPlanningStore(state => state.proposal)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const hiddenLoads = useStructuralPlanningStore(state => state.hiddenLoads)
  const visibility = useControlStore(state => state.structuralPlanningElementVisibility)
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const lintels = useModelStore(state => state.model.lintels)
  const rips = useModelStore(state => state.model.rips)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)
  const setStoreyVisibility = useModelStore(state => state.setStoreyVisibility)
  const showComments = useModelStore(state => state.showComments)
  const setShowComments = useModelStore(state => state.setShowComments)
  const showVerticalOpeningAlignmentHelpers = useModelStore(
    state => state.showVerticalOpeningAlignmentHelpers,
  )
  const verticalOpeningHelpers = useVerticalOpeningHelpers()

  const loads = useElementLoadStore(state => state.loads)

  const hiddenElements = useHiddenElements()

  const domains = useModelStore(state => state.domains)

  const toggleSingleStoreyVisibility = useModelStore(state => state.toggleSingleStoreyVisibility)
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const availableNonRoofStoreys = reject(
    [...availableStoreys],
    availStorey => availStorey === roofStoreyKey,
  )
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const addSelectedIds = useStructuralPlanningStore(state => state.addSelectedIds)
  const deselectId = useStructuralPlanningStore(state => state.deselectId)
  const selectedIds = useStructuralPlanningStore(state => state.selectedIds)

  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const activeOpening = useEditElementStore(state => state.activeOpening)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)

  const elementSelectionDisabled = useControlStore(state => state.elementSelectionDisabled)
  const storeMode = useSelectionStore(state => state.mode)

  const { isSelectionMode: isSelectionModeSnack } = useSelectionMode()

  const isTapelineActive = useTapelineStore(state => state.isActive)

  const showLoads = visibility.areaLoads || visibility.pointLoads || visibility.lineLoads

  // MEMOS

  const positions = useMemo(() => [...rips, ...lintels], [rips, lintels])

  const elementTargetDomains = useMemo(
    () =>
      reduce(
        verticalTransmissionGraph.element_targets,
        (collector, target) => ({
          ...collector,
          [target.domain_guid]: target.domain,
        }),
        {} as Record<string, Domain | null>,
      ),
    [verticalTransmissionGraph],
  )

  const wall = useMemo(() => find(model.walls, ['guid', activeElement]), [model, activeElement])

  // ACTIONS

  useTypeInteraction('none', isStiffeningSelectionMode)

  const handleModelClick = useLatest((event: ThreeEvent<MouseEvent>) => {
    const { name: guid } = event.object
    const { elementType } = event.object.userData

    if (mode === 'draw-openings') {
      setActiveElement(guid)
      return
    }

    if (mode === 'edit-openings-3d') {
      // Don't change selection when clicking on other elements in edit mode
      return
    }

    // select all roofs at once since config is the same
    const elements = [guid]
    const filteredElements = elements.filter(id => !(selectedElements || []).includes(id))

    setElements(filteredElements, elementType)
  })

  const actionMode = useControlStore(state => state.actionMode)

  // Mode-dependent selectability comes from the mode capability matrix; only the transient
  // overlays (pick flows, tapeline, hide cursor) and the explicit disable flag remain here.
  const clickEnabled = useMemo(() => {
    return (
      !elementSelectionDisabled &&
      isGenericSceneClickMode(storeMode) &&
      !isSelectionModeSnack &&
      !isTapelineActive &&
      actionMode !== 'hide'
    )
  }, [elementSelectionDisabled, storeMode, isSelectionModeSnack, isTapelineActive, actionMode])

  useModelClickListeners(
    event => {
      handleModelClick.current(event)
    },
    [handleModelClick],
    clickEnabled,
  )

  useModelClickListenerV2(
    'opening',
    (openingGuid, event, data) => {
      if (data.type !== 'opening') return
      if (mode === 'draw-openings') return

      setTypeVisibility('rips', false)
      setTypeVisibility('lintels', false)

      // One atomic selection write (opening as primary, openings mode adopted); the wall is
      // derived from the opening where needed.
      latestSetElements.current([openingGuid], 'opening')
    },
    {
      enabled: clickEnabled,
      guids: null,
    },
  )

  const guidToPositionIncludingRips = useGuidToElementIncludingRips()

  const handlePositionClickRef = useLatest((guid: string) => {
    // E.g. this could happen when transmitter target is selected
    if (isSelectionMode) return

    const element = guidToPositionIncludingRips[guid]

    if (!element) throw new Error('Element not found')
    const type = element.element_type === 'rips' ? 'rip' : 'lintel'

    latestSetElements.current([guid], type)
    return { selectedGuids: [guid] }
  })

  const handlePositionClick = useCallback(
    (guid: string) => {
      handlePositionClickRef.current(guid)
    },
    [handlePositionClickRef],
  )

  useHighlightFromSelection(!selectedStiffeningElements?.length)

  // Highlight wall when rip or lintel is selected
  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  const selectedPositionWallGuid = useMemo(() => {
    if ((isRipMode || isLintelMode) && selectedElements?.[0]) {
      const wallGuid = positionGuidToWallGuid[selectedElements[0]]
      return wallGuid ? [wallGuid] : []
    }
    return []
  }, [isRipMode, isLintelMode, selectedElements, positionGuidToWallGuid])

  useHighlightSecondary(
    selectedPositionWallGuid,
    // in vertical transmitter tab we already use secondary highlighting for the targets
    isRipMode || (isLintelMode && (!activeTab || activeTab === 'geometry')),
  )

  const handleElementClick = (event: ThreeEvent<MouseEvent>) => {
    const { localId, selectable } = event.object.userData

    if (!selectable) return

    if (selectedIds.has(localId)) deselectId(localId)
    else addSelectedIds([localId])
  }

  const handleMergedElementClick = (event: ThreeEvent<MouseEvent>) => {
    const { guid } = event.object.userData

    setStiffeningElement(guid)
  }

  // DERIVED DATA

  const filteredLoads = useMemo(() => {
    return filter(loads, load => {
      if (hiddenLoads.includes(load.guid)) return false
      if (load.load_type === 'point-load' && visibility.pointLoads) return true
      if (load.load_type === 'line-load' && visibility.lineLoads) {
        // 2D floorplan renders line loads beside their element via FloorPlan / FloorplanLineLoadsMesh.
        return floorplanStorey === null
      }
      if (load.load_type === 'area-load' && visibility.areaLoads) {
        // 2D floorplan renders area loads via FloorPlan / AreaLoadMeshElement.
        return floorplanStorey === null
      }

      return false
    })
  }, [
    loads,
    visibility.lineLoads,
    visibility.pointLoads,
    visibility.areaLoads,
    hiddenLoads,
    floorplanStorey,
  ])

  const loadsOnSelectedElement = useMemo(() => {
    if (!selectedElements?.[0]) return []

    return filter(loads, load => {
      if (load.element_guid !== selectedElements[0]) return false
      if (hiddenLoads.includes(load.guid)) return false
      if (load.load_type === 'area-load' && floorplanStorey !== null) return false

      return true
    })
  }, [loads, selectedElements, hiddenLoads, floorplanStorey])

  // EFFECTS

  // Combined single press and long press shortcuts for storey visibility
  useHotkeys_0to9WithNumpadAndLongPress(
    // Single press: toggle storey visibility
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        toggleSingleStoreyVisibility(storeyKey)
      }
    },
    // Long press: show only selected storey
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        // Set all available storeys to invisible
        availableNonRoofStoreys.forEach(storey => {
          setStoreyVisibility(storey, false)
        })
        setStoreyVisibility(roofStoreyKey, false)
        // Set the pressed storey to visible
        setStoreyVisibility(storeyKey, true)
      }
    },
    { enabled: !isOrthographic },
    [availableNonRoofStoreys],
  )
  // toggle roof storey visibility
  useHotkeys_ShortAndLongPress(
    ['r'],
    () => {
      setTypeVisibility('vertical_roof_slabs' as ElementTypes, !showVerticalRoofSlabs)
    },
    () => {
      setTypeVisibility('roof_slabs' as ElementTypes, !showRoofSlabs)
    },
    { enabled: !isOrthographic },
    [showVerticalRoofSlabs, showRoofSlabs],
  )

  useHotkeys(
    ['shift+r'],
    () => {
      toggleSingleStoreyVisibility(roofStoreyKey)
    },
    { enabled: !isOrthographic },
    [showVerticalRoofSlabs, showRoofSlabs],
  )

  useHotkeys(
    ['p'],
    () => {
      setTypeVisibility('purlins' as ElementTypes, !showPurlins)
    },
    { enabled: !isOrthographic },
    [showPurlins],
  )

  useHotkeys(
    ['c'],
    () => {
      setShowComments(!showComments)
    },
    { enabled: !isOrthographic },
    [showComments],
  )

  useHotkeys_Ctrl_0to9(
    storeyKey => {
      // A refused storey switch must not change the visibility either
      if (availableNonRoofStoreys.includes(storeyKey) && !refusePaneChange()) {
        // Close the snackbar when the user uses the hotkey
        setStoreyVisibility(roofStoreyKey, false)
        availableNonRoofStoreys.forEach(element => setStoreyVisibility(element, false))

        // set storey last
        setStorey(storeyKey)
        setStoreyVisibility(storeyKey, true)
      }
    },
    { enabled: !isOrthographic },
    [availableNonRoofStoreys],
  )

  useHotkeys(
    ['CTRL+r'],
    () => {
      // A refused storey switch must not change the visibility either
      if (refusePaneChange()) return

      // doesn't matter which storey, just set it to 0
      setStorey('0')
      // after switching storey, hide all non roof storeys
      // need to wrap in timeout to allow for the storey to be set first
      // otherwise the storey visibility will be set to true when the storey is set due to the effect of the floorplan drawer component
      setTimeout(() => {
        availableNonRoofStoreys.forEach(element => setStoreyVisibility(element, false))
        setStoreyVisibility(roofStoreyKey, true)
      }, 50)
    },
    { enabled: !isOrthographic, preventDefault: true },
    [availableNonRoofStoreys],
  )

  useHotkeys_ShortAndLongPress(
    ['d'],
    () => {
      setTypeVisibility('vertical_slabs' as ElementTypes, !showVerticalSlabs)
    },
    () => {
      setTypeVisibility('slabs' as ElementTypes, !showSlabs)
    },
    { enabled: !isOrthographic },
    [showVerticalSlabs, showSlabs],
  )

  const multiSelectElementsClb = useCallback(
    (elementGuids: string[]) => {
      multiSelectElements(elementGuids)
    },
    [multiSelectElements],
  )

  useMultiSelection({
    addElements: multiSelectElementsClb,
    removeElement: removeMultiSelectElement,
    selectedElements: isArray(selectedElements) ? (selectedElements as string[]) : [],
  })

  // COLORING

  const coloringAnchorEl = useControlStore(state => state.coloringAnchorEl)
  // We want to disable coloring when in selection mode as this is distracting
  // but only when the coloring widget is not open to give the user the ability
  // to open it
  const disableColoring = mode === 'selection' && !coloringAnchorEl
  useSceneColoring(!disableColoring)

  const onClickPositionWallLintelOrRipMode = useCallback(
    (guid: string) => {
      latestSetElements.current([guid])
      return { selectedGuids: [guid] }
    },
    [latestSetElements],
  )

  const transmittersVisibleElements = useMemo(() => {
    if (selectedElements?.length) return selectedElements

    const allGuids = [
      ...model.beams.map(beam => beam.guid),
      ...model.columns.map(column => column.guid),
      ...model.purlins.map(purlin => purlin.guid),
      ...model.vertical_slabs.map(slab => slab.guid),
      ...model.vertical_roof_slabs.map(slab => slab.guid),
      ...model.walls.map(wall => wall.guid),
      ...model.slabs.map(slab => slab.guid),
      ...model.roof_slabs.map(roofSlab => roofSlab.guid),
      ...model.lintels.map(lintel => lintel.position_guid),
      ...model.rips.map(rip => rip.position_guid),
    ]

    return allGuids.filter(guid => !hiddenElements.has(guid))
  }, [
    hiddenElements,
    model.beams,
    model.columns,
    model.lintels,
    model.purlins,
    model.rips,
    model.roof_slabs,
    model.slabs,
    model.vertical_roof_slabs,
    model.vertical_slabs,
    model.walls,
    selectedElements,
  ])

  const ifcDrawer = useIfcImportSelectionActive()
  const latestIfcDrawer = useLatest(ifcDrawer)

  const latestSetVerticalTransmitter = useLatest(setVerticalTransmitter)
  const onClickVerticalTransmitter = useCallback(
    ({ guid, element_guid }: { guid: string; element_guid: string }) => {
      if (latestIfcDrawer.current) return

      latestSetVerticalTransmitter.current(guid, element_guid)
    },
    [latestSetVerticalTransmitter, latestIfcDrawer],
  )
  const latestSetConnector = useLatest(setConnector)
  const onClickConnector = useCallback(
    ({ guid, element_guid }: { guid: string; element_guid: string }) => {
      if (latestIfcDrawer.current) return

      latestSetConnector.current(guid, element_guid)
    },
    [latestSetConnector, latestIfcDrawer],
  )

  // RENDER

  if (isStoreySelectionMode || (!storey && (isDrawingColumnsMode || isDrawingWallsMode)))
    // When jumping into a 2D only tool, we have to select a storey reference
    // first. Hence, if these tools are activated, but no storey is selected yet
    // - show the slab selection
    return <SlabSelection onClose={() => toggleMode(undefined)} />

  if (storey)
    return (
      <FloorplanDrawer
        selectedElement={selectedElements?.[0] as string | undefined}
        mode={mode as StructuralPlanningModes | undefined}
      />
    )

  if (isDrawingOpeningsMode)
    return !ifcDrawer ? (
      <>
        {mode === 'draw-openings' && activeElement && wall && (
          <>
            <OrthographicWall
              wallGuid={activeElement}
              onClickOpening={setActiveOpening}
              selectedOpenings={activeOpening ? [activeOpening] : undefined}
            />
            <OpeningsDrawer
              key={activeOpening}
              wallGuid={activeElement}
              openingGuid={activeOpening}
            />
          </>
        )}
      </>
    ) : null

  if (isWallRipMode)
    return !ifcDrawer ? (
      <>
        <VisiblePositions
          isVisibleOverwrite={guid => selectedElements?.[0] !== guid}
          onClick={onClickPositionWallLintelOrRipMode}
          selectedElements={selectedElements}
        />
      </>
    ) : null

  if (isWallLintelMode)
    return !ifcDrawer ? (
      <>
        <VisiblePositions
          isVisibleOverwrite={guid => selectedElements?.[0] !== guid}
          onClick={onClickPositionWallLintelOrRipMode}
          selectedElements={selectedElements}
        />

        <WallLintelDrawer3D selectedLintel={selectedElements?.[0] || undefined} />
      </>
    ) : null

  if (isDrawingSlabMode) {
    // component is handled via useSceneElement hook
    return null
  }

  if (isDrawingRoofMode) {
    // component is handled via useSceneElement hook
    return null
  }

  const showNeutralModeElements =
    isNeutralMode ||
    isDrawingPurlinsMode ||
    isDrawingBeamsMode ||
    isDrawingVerticalSlabsMode ||
    isDrawingVerticalRoofMode

  return (
    <>
      {isDrawingPurlinsMode && !ifcDrawer && <PurlinDrawer3D reset={reset} />}
      {isDrawingBeamsMode && <BeamDrawer3D />}
      {isDrawingVerticalSlabsMode && !ifcDrawer && (
        <VerticalSlabDrawer3D setActiveElement={setActiveElement} resetMode={reset} />
      )}
      {isDrawingVerticalRoofMode && !ifcDrawer && (
        <VerticalRoofSlabDrawer3D setActiveElement={setActiveElement} resetMode={reset} />
      )}
      {showNeutralModeElements && (
        <>
          {visibility.verticalTransmitters && (
            <>
              <VerticalTransmittersMeshConnector
                transmitterGuid={verticalTransmitter as string}
                onClick={onClickVerticalTransmitter}
                elementGuids={transmittersVisibleElements}
              />
            </>
          )}
          {visibility.stiffening && (
            <StiffeningMesh
              data={stiffeningIntervals}
              hiddenElements={hiddenElements}
              onClick={handleMergedElementClick}
              pointerPropagation
              noPointerInteractions
            />
          )}
          {visibility.connectors && (
            <>
              <TensileTransmittersMeshConnector
                transmitterGuid={connectorGuid as string}
                onClick={onClickConnector}
                elementGuids={transmittersVisibleElements}
              />
            </>
          )}

          {showLoads && (
            <LoadMesh
              loads={filteredLoads}
              domains={domains}
              elementTargetDomains={elementTargetDomains}
              onClick={setLoad}
              activeLoadGuid={activeLoadGuid as string}
              hiddenElements={hiddenElements}
              positions={positions}
            />
          )}

          <WindLoadCPEAreasMesh />

          <VisiblePositions onClick={handlePositionClick} selectedElements={selectedElements} />
        </>
      )}

      {isStiffeningSelectionMode && visibility.stiffening && (
        <StiffeningMesh
          data={proposal}
          hiddenElements={hiddenElements}
          onClick={handleElementClick}
          selectedIds={selectedIds}
        />
      )}

      {(isWallMode ||
        isVerticalSlabMode ||
        isBeamMode ||
        isRoofMode ||
        isPurlinMode ||
        isColumnMode ||
        isRipMode ||
        isLintelMode ||
        isEditOpenings3DMode ||
        isMultiEdit) && (
        <>
          {visibility.stiffening && (
            <StiffeningMesh
              key={selectedElements?.toString()}
              data={stiffeningIntervals}
              hiddenElements={hiddenElements}
              onClick={isTapelineActive ? undefined : handleMergedElementClick}
              selectedIds={selectedStiffeningElements as string[]}
              elementGuids={selectedElements as string[]}
              pointerPropagation={isSceneSelectionMode}
              noPointerInteractions={isSceneSelectionMode}
              showNonInteractable
            />
          )}

          {visibility.connectors && (
            <>
              <TensileTransmittersMeshConnector
                transmitterGuid={connectorGuid as string}
                onClick={onClickConnector}
                elementGuids={selectedElements as string[]}
              />
            </>
          )}

          <VisiblePositions onClick={handlePositionClick} selectedElements={selectedElements} />
        </>
      )}
      {isLoadMode && (
        <LoadMesh
          loads={loadsOnSelectedElement}
          domains={domains}
          elementTargetDomains={elementTargetDomains}
          onClick={setLoad}
          activeLoadGuid={activeLoadGuid as string}
          hiddenElements={hiddenElements}
          positions={positions}
        />
      )}

      {isLoadMode && (
        <VisiblePositions onClick={handlePositionClick} selectedElements={selectedElements} />
      )}

      {showVerticalOpeningAlignmentHelpers && (
        <AlignmentHelper verticalOpeningHelpers={verticalOpeningHelpers} />
      )}

      {/* Render opening editor when in edit-openings-3d mode */}
      {isEditOpenings3DMode && activeOpening && activeElement && !ifcDrawer && (
        <OpeningEdit3D key={activeOpening} wallGuid={activeElement} openingGuid={activeOpening} />
      )}
    </>
  )
}

export default StructuralPlanningScene
